<template>
  <Container title="卡号管理">
	<!-- 搜索卡片 -->
	<el-card shadow="never" class="search-card">
	  <el-form :model="searchForm" label-position="left" label-width="70px" @submit.prevent @keyup.enter="handleSearch">
		<el-row :gutter="20">
		  <!-- 企业 (仅超管) -->
		  <el-col :xs="24" :sm="12" :md="6" :lg="6" v-if="isSuperAdmin">
			<el-form-item label="企业">
			  <el-select v-model="searchForm.enterpriseId" placeholder="请选择企业" clearable style="width: 100%;" @change="handleEnterpriseChange">
				<el-option v-for="item in enterpriseList" :key="item.id" :label="item.name" :value="item.id" />
			  </el-select>
			</el-form-item>
		  </el-col>
		  
		  <!-- 站点 (仅超管) -->
		  <el-col :xs="24" :sm="12" :md="6" :lg="6" v-if="isSuperAdmin">
			<el-form-item label="站点">
			  <el-select v-model="searchForm.stationId" placeholder="请选择站点" clearable style="width: 100%;" :disabled="!searchForm.enterpriseId">
				<el-option v-for="item in stationOptions" :key="item.id" :label="item.name" :value="item.id" />
			  </el-select>
			</el-form-item>
		  </el-col>
		  
		  <!-- 提现类型 -->
		  <el-col :xs="24" :sm="12" :md="6" :lg="6">
			<el-form-item label="提现类型">
			  <el-select v-model="searchForm.withdrawType" placeholder="请选择类型" clearable style="width: 100%;">
				<el-option label="站点提现" :value="1" />
				<el-option label="企业提现" :value="2" />
			  </el-select>
			</el-form-item>
		  </el-col>
		  
		  <!-- 关键词 -->
		  <el-col :xs="24" :sm="12" :md="6" :lg="6">
			<el-form-item label="关键词">
			  <el-input v-model="searchForm.keyword" placeholder="姓名/手机号/银行卡号" clearable style="width: 100%;" />
			</el-form-item>
		  </el-col>
		  
		  <!-- 时间 -->
		  <el-col :xs="24" :sm="12" :md="6" :lg="6">
			<el-form-item label="时间">
			  <el-date-picker v-model="searchForm.dateRange" type="daterange" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" value-format="YYYY-MM-DD" :shortcuts="shortcuts" style="width: 100%;" />
			</el-form-item>
		  </el-col>
		  
		  <!-- 按钮区域：统一占 6 -->
		  <el-col :xs="24" :sm="12" :md="6" :lg="6" style="display: flex; justify-content: flex-end;">
			<el-form-item label=" " label-width="0">
			  <div style="display: flex; gap: 8px;">
				<el-button type="primary" @click="handleSearch">查询</el-button>
				<el-button @click="resetSearch">重置</el-button>
			  </div>
			</el-form-item>
		  </el-col>
		</el-row>
	  </el-form>
	</el-card>

    <!-- 表格卡片 -->
    <el-card shadow="never" class="table-card">
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <el-button v-if="$hasPermission('btn:add_card')" type="primary" @click="openAddDialog">
              <el-icon><Plus /></el-icon> 添加
            </el-button>
            <el-button v-if="isSuperAdmin" type="success" plain @click="fetchPlatformBalance" :loading="balanceLoading">
			  <el-icon><Money /></el-icon> 平台余额
			</el-button>
          </div>
          <div class="table-header-right">
            <el-tooltip content="刷新" placement="bottom"><el-button circle @click="fetchData"><el-icon><Refresh /></el-icon></el-button></el-tooltip>
            <el-tooltip content="导出" placement="bottom"><el-button circle @click="handleExport"><el-icon><Download /></el-icon></el-button></el-tooltip>
            <el-tooltip content="打印" placement="bottom"><el-button circle @click="handlePrint"><el-icon><Printer /></el-icon></el-button></el-tooltip>
          </div>
        </div>
      </template>
      <div class="table-wrapper">
        <el-table :data="tableData" border style="width: 100%">
          <el-table-column prop="id" label="" min-width="70" fixed="left" align="center" />
          <el-table-column prop="enterprise_name" label="企业名" min-width="120" show-overflow-tooltip>
            <template #default="{ row }">
              {{ row.enterprise_name || getEnterpriseName(row.enterprise_id) }}
            </template>
          </el-table-column>
          <el-table-column prop="station_name" label="站点名" min-width="120" show-overflow-tooltip />
          <el-table-column label="提现类型" min-width="100" align="center"><template #default="{ row }">{{ row.withdraw_type === 1 ? '站点提现' : '企业提现' }}</template></el-table-column>
          <el-table-column label="可提现金额" min-width="200" align="center">
            <template #default="{ row }">
              <div style="display: flex; align-items: center; justify-content: flex-end; gap: 6px;">
                <span><span v-if="row.withdraw_type === 1">站点余额: {{ Number(row.available_balance || 0).toFixed(2) }}</span><span v-else>企业余额: {{ Number(row.available_balance || 0).toFixed(2) }}</span></span>
                <span class="withdraw-link" @click="handleWithdraw(row)">提现</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="holder_name" label="持卡人" min-width="100" show-overflow-tooltip />
          <el-table-column prop="phone" label="手机号" min-width="120" />
          <el-table-column prop="bank_card" label="银行卡号" min-width="180" show-overflow-tooltip />
          <el-table-column prop="bank_name" label="银行名称" min-width="120" show-overflow-tooltip />
          <el-table-column prop="branch_name" label="支行名称" min-width="120" show-overflow-tooltip />
          <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip />
          <el-table-column prop="created_at" label="创建时间" min-width="160">
            <template #default="{ row }">{{ formatTime(row.created_at) }}</template>
          </el-table-column>
          <el-table-column label="钱包状态" min-width="90" align="center">
            <template #default="{ row }">
              <el-tag v-if="row.wallet_merchant_no && row.link_id" type="success">已绑卡</el-tag>
              <el-tag v-else-if="row.wallet_merchant_no" type="warning">已注册</el-tag>
              <el-tag v-else type="info">未注册</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="钱包余额" min-width="120" align="right">
            <template #default="{ row }">
              <span v-if="row.wallet_merchant_no" @click="fetchWalletBalance(row)" style="cursor:pointer;color:#409EFF;">
                {{ row.walletBalance !== undefined ? Number(row.walletBalance).toFixed(2) : '点击查询' }}
              </span>
              <span v-else>钱包未注册</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" min-width="560" align="center">
            <template #default="{ row }">
              <div class="action-btns">
                <el-button v-if="$hasPermission('btn:edit_card')" type="primary" size="small" plain @click="openEditDialog(row)">修改</el-button>
                <el-button v-if="$hasPermission('btn:add_card')" type="success" size="small" plain @click="handleCopy(row)">复制卡信息</el-button>

                <template v-if="!row.wallet_merchant_no">
                  <el-button type="success" size="small" plain @click="handleWalletRegister(row)">注册钱包</el-button>
                </template>
                <template v-else-if="!row.contract_signed">
                  <el-button type="warning" size="small" plain @click="handleWalletContractSign(row)">申请签约</el-button>
                </template>
                <template v-else-if="!row.link_id">
                  <el-button type="primary" size="small" plain @click="handleWalletBind(row)">绑卡</el-button>
                </template>
                <template v-else>
                  <el-button type="info" size="small" plain disabled>已绑定</el-button>
                </template>

                <el-button
                  v-if="row.wallet_merchant_no && row.contract_signed"
                  type="success"
                  size="small"
                  plain
                  @click="handleWalletTransfer(row)"
                >转账</el-button>

                <el-button
                  v-if="row.wallet_merchant_no"
                  type="info"
                  size="small"
                  plain
                  @click="handleWalletInfo(row)"
                >查询钱包</el-button>
                <el-button v-if="$hasPermission('btn:delete_card')" type="danger" size="small" plain @click="handleDelete(row)">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="page.current"
          v-model:page-size="page.pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="onSizeChange"
          @current-change="fetchData"
        />
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="720px"
      @close="closeDialog"
      destroy-on-close
      class="card-form-dialog"
    >
      <el-form
        :model="form"
        :rules="rules"
        ref="formRef"
        label-width="140px"
        label-position="right"
        class="card-form"
      >
        <el-form-item label="提现类型：" prop="withdraw_type">
          <el-radio-group v-model="form.withdraw_type" @change="onWithdrawTypeChange">
            <el-radio :value="1" border>站点提现</el-radio>
            <el-radio :value="2" border>企业提现</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="form.withdraw_type === 1" label="站点：" prop="station_id">
          <el-select v-model="form.station_id" placeholder="请选择站点" style="width: 65%;">
            <el-option v-for="item in formStationOptions" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>

        <el-form-item v-if="form.withdraw_type === 2" label="企业：" prop="enterprise_id">
          <el-select v-model="form.enterprise_id" placeholder="请选择企业" style="width: 65%;">
            <el-option v-for="item in formEnterpriseList" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="姓名：" prop="holder_name">
          <el-input v-model="form.holder_name" placeholder="请输入姓名" style="width: 65%;" />
        </el-form-item>

        <el-form-item label="身份证号：" prop="id_card">
          <el-input v-model="form.id_card" placeholder="请输入身份证号" style="width: 65%;" />
        </el-form-item>

        <el-form-item label="有效期限类型：" prop="valid_period_type">
          <el-radio-group v-model="form.valid_period_type">
            <el-radio :value="1" border>非长期有效</el-radio>
            <el-radio :value="2" border>长期有效</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="有效期开始日期：" prop="valid_start">
          <el-date-picker
            v-model="form.valid_start"
            type="date"
            placeholder="请选择有效期开始日期"
            value-format="x"
            style="width: 65%;"
          />
        </el-form-item>

        <el-form-item v-if="form.valid_period_type === 1" label="有效期截止日期：" prop="valid_end">
          <el-date-picker
            v-model="form.valid_end"
            type="date"
            placeholder="请选择有效期截止日期"
            value-format="x"
            style="width: 65%;"
          />
        </el-form-item>

        <el-form-item label="银行卡号：" prop="bank_card">
          <el-input v-model="form.bank_card" placeholder="请输入银行卡号" style="width: 65%;" />
        </el-form-item>

        <el-form-item label="卡号地址：" prop="bank_province">
          <el-cascader
            v-model="form.region"
            :options="areaOptions"
            :props="cascaderProps"
            placeholder="请选择省/市/区"
            clearable
            style="width: 65%;"
            @change="handleRegionChange"
          />
        </el-form-item>

        <el-form-item label="开户行行号：" prop="bank_code">
          <el-input v-model="form.bank_code" placeholder="请输入开户行行号" style="width: 65%;" />
        </el-form-item>

        <el-form-item label="银行名称：" prop="bank_name">
          <el-input v-model="form.bank_name" placeholder="请输入银行名称" style="width: 65%;" />
        </el-form-item>

        <el-form-item label="支行名称：" prop="branch_name">
          <el-input v-model="form.branch_name" placeholder="请输入支行名称" style="width: 65%;" />
        </el-form-item>

        <el-form-item label="手机号：" prop="phone">
          <el-input v-model="form.phone" placeholder="请输入手机号" style="width: 65%;" />
        </el-form-item>

        <el-form-item label="身份证正面：" prop="id_card_front">
          <el-upload class="avatar-uploader" :action="uploadUrl" :show-file-list="false"
            :on-success="(res) => { form.id_card_front = res.data }">
            <img v-if="form.id_card_front" :src="fileurl + form.id_card_front" class="avatar" />
            <i v-else class="el-icon-plus avatar-uploader-icon"><el-icon><Plus /></el-icon></i>
          </el-upload>
        </el-form-item>

        <el-form-item label="身份证反面：" prop="id_card_back">
          <el-upload class="avatar-uploader" :action="uploadUrl" :show-file-list="false"
            :on-success="(res) => { form.id_card_back = res.data }">
            <img v-if="form.id_card_back" :src="fileurl + form.id_card_back" class="avatar" />
            <i v-else class="el-icon-plus avatar-uploader-icon"><el-icon><Plus /></el-icon></i>
          </el-upload>
        </el-form-item>

        <el-form-item label="结算卡正面：" prop="bank_card_front">
          <el-upload class="avatar-uploader" :action="uploadUrl" :show-file-list="false"
            :on-success="(res) => { form.bank_card_front = res.data }">
            <img v-if="form.bank_card_front" :src="fileurl + form.bank_card_front" class="avatar" />
            <i v-else class="el-icon-plus avatar-uploader-icon"><el-icon><Plus /></el-icon></i>
          </el-upload>
        </el-form-item>

        <el-form-item label="结算卡反面：" prop="bank_card_back">
          <el-upload class="avatar-uploader" :action="uploadUrl" :show-file-list="false"
            :on-success="(res) => { form.bank_card_back = res.data }">
            <img v-if="form.bank_card_back" :src="fileurl + form.bank_card_back" class="avatar" />
            <i v-else class="el-icon-plus avatar-uploader-icon"><el-icon><Plus /></el-icon></i>
          </el-upload>
        </el-form-item>

        <el-form-item label="门头照：" prop="shop_front">
          <el-upload class="avatar-uploader" :action="uploadUrl" :show-file-list="false"
            :on-success="(res) => { form.shop_front = res.data }">
            <img v-if="form.shop_front" :src="fileurl + form.shop_front" class="avatar" />
            <i v-else class="el-icon-plus avatar-uploader-icon"><el-icon><Plus /></el-icon></i>
          </el-upload>
        </el-form-item>

        <el-form-item label="店铺前台：" prop="shop_interior">
          <el-upload class="avatar-uploader" :action="uploadUrl" :show-file-list="false"
            :on-success="(res) => { form.shop_interior = res.data }">
            <img v-if="form.shop_interior" :src="fileurl + form.shop_interior" class="avatar" />
            <i v-else class="el-icon-plus avatar-uploader-icon"><el-icon><Plus /></el-icon></i>
          </el-upload>
        </el-form-item>

        <el-form-item label="店铺内部：" prop="shop_interior2">
          <el-upload class="avatar-uploader" :action="uploadUrl" :show-file-list="false"
            :on-success="(res) => { form.shop_interior2 = res.data }">
            <img v-if="form.shop_interior2" :src="fileurl + form.shop_interior2" class="avatar" />
            <i v-else class="el-icon-plus avatar-uploader-icon"><el-icon><Plus /></el-icon></i>
          </el-upload>
        </el-form-item>

        <el-form-item label="备注：" prop="remark">
          <el-input v-model="form.remark" type="textarea" :rows="3" placeholder="请输入备注" style="width: 65%;" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="closeDialog">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">保存</el-button>
      </template>
    </el-dialog>

    <!-- 平台余额弹窗 -->
    <el-dialog
      v-model="balanceDialogVisible"
      title="平台可用余额"
      width="560px"
      :close-on-click-modal="false"
    >
      <div v-if="platformBalanceDetail" style="padding: 10px 0;">
        <div style="text-align: center; padding: 16px 0 20px; border-bottom: 1px dashed #e4e7ed;">
          <div style="font-size: 13px; color: #909399; margin-bottom: 6px;">
            账户号：{{ platformBalanceDetail.merchantNo || '--' }}
          </div>
          <div style="font-size: 14px; color: #606266; margin-bottom: 8px;">账户总金额</div>
          <div style="font-size: 30px; font-weight: 600; color: #409EFF;">
            {{ platformBalanceDetail.totalAmount || '0.00' }}
          </div>
          <div style="font-size: 13px; color: #909399; margin-top: 6px;">
            已结算未付款金额：{{ platformBalanceDetail.settledUnpaidAmount || '0.00' }} 元
          </div>
        </div>

        <div style="margin-top: 16px;">
          <div
            v-for="(acc, idx) in platformBalanceDetail.accountList || []"
            :key="idx"
            style="margin-bottom: 12px; padding: 12px; background: #f5f7fa; border-radius: 4px;"
          >
            <div style="font-size: 14px; font-weight: 600; color: #303133; margin-bottom: 10px;">
              {{ acc.accountType === '01' ? '一般消费类账户' : acc.accountType === '02' ? '待结算账户' : '账户' }}
              <span style="font-size: 12px; color: #909399; font-weight: normal; margin-left: 8px;">
                ID: {{ acc.accountId }}
              </span>
            </div>
            <el-row :gutter="12">
              <el-col :span="12" style="margin-bottom: 8px;">
                <div style="font-size: 12px; color: #909399;">可提现金额</div>
                <div style="font-size: 16px; font-weight: 600; color: #67C23A;">{{ acc.cashAmount || '0.00' }}</div>
              </el-col>
              <el-col :span="12" style="margin-bottom: 8px;">
                <div style="font-size: 12px; color: #909399;">不可提现金额</div>
                <div style="font-size: 16px; font-weight: 600; color: #E6A23C;">{{ acc.uncashAmount || '0.00' }}</div>
              </el-col>
              <el-col :span="12">
                <div style="font-size: 12px; color: #909399;">冻结可提现金额</div>
                <div style="font-size: 16px; font-weight: 600; color: #F56C6C;">{{ acc.freezeCashAmount || '0.00' }}</div>
              </el-col>
              <el-col :span="12">
                <div style="font-size: 12px; color: #909399;">冻结不可提现金额</div>
                <div style="font-size: 16px; font-weight: 600; color: #F56C6C;">{{ acc.freezeUncashAmount || '0.00' }}</div>
              </el-col>
            </el-row>
            <div style="font-size: 12px; color: #C0C4CC; margin-top: 8px;">
              最近资金变动：{{ acc.amountChangetime || '-' }}
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="balanceDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="fetchPlatformBalance" :loading="balanceLoading">刷新</el-button>
      </template>
    </el-dialog>

    <!-- 入网详情弹窗 -->
    <el-dialog v-model="detailDialogVisible" title="入网详情" width="500px" draggable :close-on-click-modal="false">
      <div v-if="detailData" style="padding: 10px 0;">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="申请单号">{{ detailData.application_no || '-' }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getOnboardStatusType(detailData.status)" size="small">
              {{ getOnboardStatusText(detailData.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="渠道单号">{{ detailData.channel_apply_no || '-' }}</el-descriptions-item>
          <el-descriptions-item label="驳回原因">{{ detailData.reject_reason || detailData.remark || '无' }}</el-descriptions-item>
          <el-descriptions-item label="银盛商户号" v-if="detailData.merchant_no">{{ detailData.merchant_no }}</el-descriptions-item>
        </el-descriptions>
      </div>
      <template #footer>
        <el-button @click="detailDialogVisible = false">确定</el-button>
      </template>
    </el-dialog>

    <!-- 钱包信息弹窗 -->
    <el-dialog v-model="walletInfoDialogVisible" title="钱包信息" width="650px" draggable :close-on-click-modal="false">
      <div v-if="walletInfoData" style="padding: 10px 0;">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="钱包商户号">{{ walletInfoData.merchantNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="姓名">{{ walletInfoData.name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="身份证号">{{ walletInfoData.certNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ walletInfoData.mobile || '-' }}</el-descriptions-item>
          <el-descriptions-item label="认证等级">{{ walletInfoData.authLevel || '-' }}</el-descriptions-item>
          <el-descriptions-item label="账户状态">{{ walletInfoData.accountState || '-' }}</el-descriptions-item>
          <el-descriptions-item label="签约状态">{{ walletInfoData.contractState || '-' }}</el-descriptions-item>
          <el-descriptions-item label="开户时间">{{ walletInfoData.openTime || '-' }}</el-descriptions-item>
        </el-descriptions>
        <div style="margin-top: 16px; font-weight: 600; font-size: 14px;">绑定银行卡列表</div>
        <el-table :data="walletInfoData.bankCardList || []" border style="margin-top: 8px;" size="small">
          <el-table-column prop="linkId" label="绑卡标识" min-width="120" />
          <el-table-column prop="bankAccountNo" label="银行卡号" min-width="150" />
          <el-table-column prop="bankName" label="银行名称" min-width="120" />
          <el-table-column prop="bankMobile" label="预留手机号" min-width="120" />
          <el-table-column prop="bindFunction" label="绑定功能" min-width="100">
            <template #default="{ row }">
              {{ row.bindFunction === '01' ? '提现+消费' : row.bindFunction === '02' ? '仅提现' : row.bindFunction === '03' ? '仅消费' : row.bindFunction }}
            </template>
          </el-table-column>
          <el-table-column prop="bindTime" label="绑定时间" min-width="160" />
        </el-table>
      </div>
      <template #footer>
        <el-button @click="walletInfoDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 转账弹窗 -->
    <el-dialog
      v-model="transferDialogVisible"
      :title="transferStage === 'input' ? '平台转账到个人钱包' : '交易密码校验'"
      width="720px"
      :close-on-click-modal="false"
      :before-close="handleTransferDialogClose"
      @close="resetTransferDialog"
    >
      <div v-if="transferStage === 'input'" style="padding: 10px 0;">
        <div style="margin-bottom: 16px; font-size: 13px; color: #606266; line-height: 1.9;">
          持卡人：<b>{{ transferRow?.holder_name }}</b><br/>
          钱包账号：{{ transferRow?.wallet_merchant_no }}<br/>
          账户总金额：<span style="color: #409EFF; font-weight: 600;">{{ platformTotalBalance }}</span> 元<br/>
          可提现金额：<span style="color: #67C23A; font-weight: 600; font-size: 15px;">{{ platformAvailableBalance }}</span> 元
          <span style="color:#909399;font-size:12px;">（实际可转账上限）</span>
        </div>
        <el-input v-model="transferAmount" placeholder="请输入转账金额" clearable>
          <template #prepend>金额</template>
          <template #append>元</template>
        </el-input>
        <div style="font-size: 12px; color: #909399; margin-top: 8px;">
          提示：仅"可提现金额"可用于转账，待结算金额（D+1）到账后方可转出。
        </div>
      </div>

      <div v-else-if="transferStage === 'pwd'" style="padding: 0;">
        <div style="font-size: 13px; color: #606266; margin-bottom: 8px;">
          请在下方页面输入交易密码完成验证（如未显示，请点击下方按钮在新窗口打开）
        </div>
        <iframe
          :src="transferPageUrl"
          style="width: 100%; height: 520px; border: 1px solid #e4e7ed; border-radius: 4px;"
          frameborder="0"
        ></iframe>
        <div style="text-align: right; margin-top: 8px;">
          <el-button link type="primary" @click="openPwdInNewTab">在新窗口打开</el-button>
        </div>
      </div>

      <div v-else-if="transferStage === 'waiting'" style="text-align: center; padding: 40px 0;">
        <el-icon class="is-loading" :size="32"><Loading /></el-icon>
        <div style="margin-top: 12px; color: #606266;">正在等待银行处理结果...</div>
      </div>

      <template #footer>
        <el-button @click="transferDialogVisible = false">关闭</el-button>
        <el-button
          v-if="transferStage === 'input'"
          type="primary"
          @click="submitTransfer"
          :loading="transferSubmitting"
        >确认转账</el-button>
        <el-button
          v-else-if="transferStage === 'waiting'"
          type="primary"
          @click="queryTransferResult"
        >查询结果</el-button>
      </template>
    </el-dialog>

    <!-- 提现弹窗 -->
    <el-dialog v-model="withdrawDialogVisible" title="提现" width="420px" :close-on-click-modal="false">
      <div style="padding: 10px 0;">
        <div style="margin-bottom: 16px; font-size: 14px; color: #606266;">可提现余额: <span style="font-weight: 600; color: #303133;">{{ withdrawBalance }}</span></div>
        <el-input v-model="withdrawAmount" :placeholder="`请输入要给【${withdrawHolderName}】提现的金额`" clearable>
          <template #prepend>金额</template>
        </el-input>
      </div>
      <template #footer>
        <el-button @click="withdrawDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitWithdraw" :loading="withdrawSubmitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- 导出弹窗 -->
    <ExportDialog
      v-model:visible="exportDialogVisible"
      :fields="exportFields"
      :export-api="exportApi"
      :get-search-params="getSearchParams"
      :current-page="page"
      :default-file-name="defaultFileName()"
    />
  </Container>
</template>

<script>
const pca = require('china-division/dist/pca.json');

// ★ 模块顶层构建一次，避免每次实例化都遍历几 MB JSON
function buildAreaOptions(data) {
  if (!data || typeof data !== 'object') return [];
  const provinceList = [];
  for (const [provinceName, cities] of Object.entries(data)) {
    if (typeof cities === 'object' && cities !== null) {
      const cityList = [];
      for (const [cityName, districts] of Object.entries(cities)) {
        const children = Array.isArray(districts)
          ? districts.map(d => ({ name: d }))
          : [];
        cityList.push({
          name: cityName,
          children: children.length > 0 ? children : undefined,
        });
      }
      provinceList.push({
        name: provinceName,
        children: cityList.length > 0 ? cityList : undefined,
      });
    }
  }
  return provinceList;
}
const AREA_OPTIONS = buildAreaOptions(pca);

import { pub } from '/common/event';
import moment from 'moment';
import { Plus, Refresh, Download, Printer, Loading } from '@element-plus/icons-vue';
import { dateShortcuts } from '../../../utils/shortcuts.js';
import ExportDialog from '@/components/container/ExportDialog.vue';
import exportMixin from '@/mixins/exportMixin';
import enterpriseStationMixin from '@/mixins/enterpriseStationMixin';
import { getUploadUrl } from '@/utils/upload';

export default {
  components: { Plus, Refresh, Download, Printer, ExportDialog },
  mixins: [exportMixin, enterpriseStationMixin],
  data() {
    return {
      shortcuts: dateShortcuts,
      searchForm: { enterpriseId: null, stationId: null, withdrawType: null, keyword: '', dateRange: null },
      formEnterpriseList: [],
      formStationOptions: [],
      tableData: [],
      total: 0,
      page: { current: 1, pageSize: 15 },
      dialogVisible: false,
      dialogTitle: '添加卡号',
      detailDialogVisible: false,
      detailData: null,
      editId: null,
      platformBalance: null,
      platformMerchantNo: '',
      balanceLoading: false,
      balanceDialogVisible: false,
      platformBalanceDetail: null,
      transferDialogVisible: false,
      platformAvailableBalance: '0.00',
      platformTotalBalance: '0.00',
      transferRow: null,
      transferAmount: '',
      transferSubmitting: false,
      transferStage: 'input',
      transferPageUrl: '',
      transferRequestNo: '',
      transferPollTimer: null,
      form: {
        withdraw_type: 1, enterprise_id: null, station_id: null, holder_name: '', id_card: '',
        valid_period_type: 1, valid_start: null, valid_end: null, bank_card: '',
        bank_province: '', bank_city: '', bank_code: '', bank_name: '', branch_name: '', phone: '',
        id_card_front: '', id_card_back: '', remark: '', huifu_id: '', yisheng_id: '', status: 1,
        region: [],
      },
      rules: {
        holder_name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        id_card: [{ required: true, message: '请输入身份证号', trigger: 'blur' }],
        bank_card: [{ required: true, message: '请输入银行卡号', trigger: 'blur' }],
        phone: [{ required: true, message: '请输入手机号', trigger: 'blur' }],
        station_id: [{ required: true, message: '请选择站点', trigger: 'change' }],
        enterprise_id: [{ required: true, message: '请选择企业', trigger: 'change' }],
        valid_start: [{ required: true, message: '请选择有效期开始日期', trigger: 'change' }],
        valid_end: [{ required: true, message: '请选择有效期截止日期', trigger: 'change' }],
      },
      submitting: false,
      uploadUrl: '',
      withdrawDialogVisible: false,
      withdrawAmount: '',
      withdrawHolderName: '',
      withdrawBalance: '0.00',
      withdrawRow: null,
      withdrawSubmitting: false,

      // ★ 引用模块顶层常量
      areaOptions: AREA_OPTIONS,
      cascaderProps: {
        label: 'name',
        value: 'name',
        children: 'children',
      },
      exportFields: [
        { key: 'id', label: '序号列', checked: true },
        { key: 'card_id', label: '卡ID', checked: true },
        { key: 'enterprise_name', label: '企业名', checked: true },
        { key: 'station_name', label: '站点名', checked: true },
        { key: 'withdraw_type_label', label: '提现类型', checked: true },
        { key: 'holder_name', label: '持卡人', checked: true },
        { key: 'phone', label: '手机号', checked: true },
        { key: 'bank_card', label: '银行卡号', checked: true },
        { key: 'bank_province', label: '卡号省份', checked: true },
        { key: 'bank_city', label: '卡号城市', checked: true },
        { key: 'bank_district', label: '区/县', checked: true },
        { key: 'bank_code', label: '开户行行号', checked: true },
        { key: 'bank_name', label: '银行名称', checked: true },
        { key: 'branch_name', label: '支行名称', checked: true },
        { key: 'remark', label: '备注', checked: true },
        { key: 'created_at_label', label: '创建时间', checked: true },
        { key: 'updated_at_label', label: '更新时间', checked: true },
        { key: 'huifu_id', label: '银盛ID', checked: true },
      ],
      walletInfoDialogVisible: false,
      walletInfoData: null,
    };
  },
  computed: {
    // ★ 从空方法改成正解（兼容原有模板/逻辑）
    userInfo() {
      try {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : {};
      } catch (e) {
        return {};
      }
    },
    fileurl() {
      return this.$store.state.common.systemInfo?.fileurl || '';
    },
    uploadHeaders() {
      return { Authorization: 'Bearer ' + (this.$store.state.user?.token || '') };
    },
    exportApi() {
      return this.$api.card.export;
    },
	isSuperAdmin() {
	  const u1 = this.$store.state.user?.user;
	  if (u1 && u1.role) return u1.role === 'super_admin';
	  try {
		const u2 = JSON.parse(localStorage.getItem('user') || '{}');
		return u2.role === 'super_admin' || u2.role_id === 1;
	  } catch (e) { return false; }
	},
  },
  mounted() {
    // ★ 用 getUploadUrl 自动处理空端口
    this.uploadUrl = getUploadUrl('image');
	// ★ 非超管，自动带入自己的企业或站点参数
	if (!this.isSuperAdmin) {
	  try {
		const u = JSON.parse(localStorage.getItem('user') || '{}');
		if (u.role === 'admin' && u.enterprise_id) {
		  this.searchForm.enterpriseId = u.enterprise_id;
		} else if (u.role === 'station_leader' && u.station_id) {
		  this.searchForm.stationId = u.station_id;
		}
	  } catch (e) {}
	}
    this.loadFormEnterprises();
    this.loadFormStations();
    this.fetchData();
  },
  beforeUnmount() {
    // ★ 清理转账定时器 + window message 监听
    if (this.transferPollTimer) {
      clearInterval(this.transferPollTimer);
      this.transferPollTimer = null;
    }
    if (this._transferMsgListener) {
      window.removeEventListener('message', this._transferMsgListener);
      this._transferMsgListener = null;
    }
  },
  methods: {
    defaultFileName() {
      return '卡号数据';
    },

    // ★ 删除 buildAreaOptions（已提到模块顶层）

    handleRegionChange(value) {
      if (value && value.length > 0) {
        this.form.bank_province = value[0] || '';
        this.form.bank_city = value[1] || '';
        this.form.bank_district = value[2] || '';
      } else {
        this.form.bank_province = '';
        this.form.bank_city = '';
        this.form.bank_district = '';
      }
    },

    formatTime(ts) {
      if (!ts) return '';
      return moment(ts).format('YYYY-MM-DD HH:mm:ss');
    },

    getEnterpriseName(id) {
      if (!id) return '';
      const found = this.formEnterpriseList.find(item => item.id === id);
      return found ? found.name : '';
    },

    async loadFormEnterprises() {
      try {
        const ret = await this.$api.enterprise.page({ status: 1, current: 1, pageSize: 999 });
        if (ret && ret.success) {
          this.formEnterpriseList = ret.data?.data || ret.data || [];
        }
      } catch (e) {
        console.error('加载企业列表失败:', e);
      }
    },

    async loadFormStations() {
      try {
        const ret = await this.$api.station.page({ status: 1, pageSize: 1000 });
        if (ret && ret.success) {
          this.formStationOptions = ret.data?.data || ret.data || [];
        }
      } catch (e) {
        console.error('加载站点列表失败:', e);
      }
    },

    onWithdrawTypeChange(val) {
      this.form.enterprise_id = null;
      this.form.station_id = null;
    },

    async fetchData() {
      try {
        const params = {
          page: this.page.current,
          pageSize: this.page.pageSize,
          stationId: this.searchForm.stationId || undefined,
          enterpriseId: this.searchForm.enterpriseId || undefined,
          withdrawType: this.searchForm.withdrawType || undefined,
          keyword: this.searchForm.keyword || undefined,
        };
        if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
          params.startTime = new Date(this.searchForm.dateRange[0]).getTime();
          params.endTime = new Date(this.searchForm.dateRange[1]).getTime() + 24 * 60 * 60 * 1000 - 1;
        }
        const ret = await this.$api.card.page(params);
        if (ret && ret.success) {
          this.tableData = ret.data.data || [];
          this.total = ret.data.total || 0;
          if (ret.data.page) {
            this.page.current = ret.data.page.current;
            this.page.pageSize = ret.data.page.pageSize;
          }
          if (this.formEnterpriseList.length) {
            this.tableData.forEach(item => {
              if (!item.enterprise_name && item.enterprise_id) {
                const ent = this.formEnterpriseList.find(e => e.id === item.enterprise_id);
                if (ent) item.enterprise_name = ent.name;
              }
            });
          }
        } else {
          pub('error_message', ret?.message || '加载数据失败');
        }
      } catch (e) {
        console.error('加载卡号列表失败:', e);
        pub('error_message', '请求失败');
      }
    },

    // ★ 新增：分页 size 变化回第 1 页
    onSizeChange() {
      this.page.current = 1;
      this.fetchData();
    },

    handleSearch() {
      this.page.current = 1;
      this.fetchData();
    },

    resetSearch() {
      this.searchForm = { enterpriseId: null, stationId: null, withdrawType: null, keyword: '', dateRange: null };
      this.stationOptions = [];
      this.page.current = 1;
      this.fetchData();
    },

    openAddDialog() {
      this.dialogTitle = '添加卡号';
      this.editId = null;
      this.resetForm();
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.formRef?.clearValidate();
      });
    },

    openEditDialog(row) {
      this.dialogTitle = '编辑卡号';
      this.editId = row.id;
      this.form = { ...row };
      if (row.bank_province) {
        const region = [row.bank_province];
        if (row.bank_city) region.push(row.bank_city);
        if (row.bank_district) region.push(row.bank_district);
        this.form.region = region;
      }
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.formRef?.clearValidate();
      });
    },

    closeDialog() {
      this.dialogVisible = false;
      this.editId = null;
      this.resetForm();
    },

    async handleCopy(row) {
      try {
        await this.$confirm(
          `确定复制此卡号信息吗？复制后可修改站点等差异项。`,
          '复制卡信息',
          { confirmButtonText: '确定复制', cancelButtonText: '取消', type: 'info' }
        );
      } catch (e) {
        return;
      }

      try {
        const ret = await this.$api.card.copy({ id: row.id });
        if (ret && ret.success) {
          pub('success_message', '复制成功，请修改站点等差异信息');
          await this.fetchData();
        } else {
          pub('error_message', ret?.message || '复制失败');
        }
      } catch (e) {
        console.error('复制卡信息失败:', e);
        pub('error_message', '请求失败');
      }
    },

    resetForm() {
      this.form = {
        withdraw_type: 1,
        enterprise_id: null,
        station_id: null,
        holder_name: '',
        id_card: '',
        valid_period_type: 1,
        valid_start: null,
        valid_end: null,
        bank_card: '',
        bank_province: '',
        bank_city: '',
        bank_district: '',
        bank_code: '',
        bank_name: '',
        branch_name: '',
        phone: '',
        id_card_front: '',
        id_card_back: '',
        remark: '',
        status: 1,
        region: [],
      };
    },

    async submitForm() {
      try {
        await this.$refs.formRef.validate();
      } catch (e) {
        return;
      }
      this.submitting = true;
      try {
        const data = { ...this.form };
        delete data.region;

        if (data.withdraw_type === 1) {
          data.enterprise_id = 0;
        } else {
          data.station_id = 0;
        }
        delete data.enterprise_name;
        delete data.station_name;
        delete data.created_at;
        delete data.updated_at;

        let ret;
        if (this.editId) {
          data.id = this.editId;
          ret = await this.$api.card.update(data);
        } else {
          ret = await this.$api.card.create(data);
        }
        if (ret && ret.success) {
          pub('success_message', this.editId ? '编辑成功' : '添加成功');
          this.closeDialog();
          this.fetchData();
        } else {
          pub('error_message', ret?.message || '操作失败');
        }
      } catch (e) {
        console.error('提交失败:', e);
        pub('error_message', '请求失败');
      } finally {
        this.submitting = false;
      }
    },

    handleDelete(row) {
      this.$confirm('确定要删除该卡号吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          try {
            const ret = await this.$api.card.delete(row.id);
            if (ret && ret.success) {
              pub('success_message', '删除成功');
              this.fetchData();
            } else {
              pub('error_message', ret?.message || '删除失败');
            }
          } catch (e) {
            pub('error_message', '请求失败');
          }
        })
        .catch(() => {});
    },

    handleWithdraw(row) {
      this.withdrawHolderName = row.holder_name || '未知';
      // ★ 强制转数字，避免后端返回字符串导致 toFixed 崩溃
      const balance = Number(row.available_balance) || 0;
      this.withdrawBalance = balance.toFixed(2);
      this.withdrawRow = row;
      this.withdrawAmount = '';
      this.withdrawDialogVisible = true;
    },

    async submitWithdraw() {
      if (!this.withdrawAmount || parseFloat(this.withdrawAmount) <= 0) {
        pub('error_message', '请输入有效的提现金额');
        return;
      }
      const amount = parseFloat(this.withdrawAmount);
      if (amount > parseFloat(this.withdrawBalance)) {
        pub('error_message', '提现金额不能超过可提现余额');
        return;
      }
      this.withdrawSubmitting = true;
      try {
        const params = {
          cardId: this.withdrawRow.id,
          withdrawType: this.withdrawRow.withdraw_type,
          withdrawAmount: amount,
          enterpriseId: this.withdrawRow.enterprise_id || 0,
          stationId: this.withdrawRow.station_id || 0,
          holderName: this.withdrawRow.holder_name,
          bankCard: this.withdrawRow.bank_card,
          merchantNo: this.withdrawRow.huifu_id || '',
          remark: '',
        };
        const ret = await this.$api.withdraw.apply(params);
        if (ret && ret.success) {
          pub('success_message', '提现申请已提交，等待审核');
          this.withdrawDialogVisible = false;
          this.fetchData();
        } else {
          pub('error_message', ret?.message || '提现申请失败');
        }
      } catch (e) {
        console.error('提现申请异常', e);
        pub('error_message', '请求失败');
      } finally {
        this.withdrawSubmitting = false;
      }
    },

    handleExport() {
      this.openExportDialog();
    },
    handlePrint() {
      window.print();
    },

    // ★ 删除 getSearchParams（mixin 接管）

    async handleOnboard(row) {
      try {
        const ret = await this.$api.card.onboardDetail(row.id);
        if (ret && ret.success && ret.data && ['UNDER_REVIEW', 'PENDING_SIGN', 'MANUAL_REVIEW'].includes(ret.data.status)) {
          this.$message.warning('该卡已有进行中的入网申请，请勿重复提交');
          return;
        }
      } catch (e) {}

      await this.$confirm('确定要为该持卡人提交银盛小微入网申请吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info',
      });

      try {
        const ret = await this.$api.card.onboard(row.id);
        if (ret && ret.success) {
          this.$message.success('入网申请已提交，请等待审核');
          this.fetchData();
        } else {
          this.$message.error(ret?.message || '入网申请失败');
        }
      } catch (e) {
        this.$message.error('请求异常');
      }
    },

    getOnboardStatusText(status) {
      const map = {
        'UNDER_REVIEW': '审核中',
        'PENDING_SIGN': '待签约',
        'SIGNED': '已签约',
        'MANUAL_REVIEW': '人工审核',
        'REJECTED': '已驳回',
        'FAILED': '失败',
      };
      return map[status] || status;
    },
    getOnboardStatusType(status) {
      const map = {
        'UNDER_REVIEW': 'warning',
        'PENDING_SIGN': 'info',
        'SIGNED': 'success',
        'MANUAL_REVIEW': 'warning',
        'REJECTED': 'danger',
        'FAILED': 'danger',
      };
      return map[status] || '';
    },

    async handleOnboardDetail(row) {
      try {
        const ret = await this.$api.card.onboardDetail(row.id);
        if (ret && ret.success && ret.data) {
          this.detailData = ret.data;
          if (ret.data.extra_data) {
            try {
              const extra = JSON.parse(ret.data.extra_data);
              if (extra.rejectReason) this.detailData.reject_reason = extra.rejectReason;
              else if (extra.remark) this.detailData.reject_reason = extra.remark;
            } catch (e) {}
          }
          this.detailDialogVisible = true;
        } else {
          this.$message.info('暂无入网记录');
        }
      } catch (e) {
        this.$message.error('获取详情失败');
      }
    },

    async handleWalletRegister(row) {
      let mobile = row.phone || '';

      if (mobile) {
        try {
          await this.$confirm(`将使用已保存的手机号 ${mobile} 注册钱包，确认吗？`, '提示', {
            confirmButtonText: '确认',
            cancelButtonText: '修改',
            type: 'info',
          });
        } catch (e) {
          try {
            const { value } = await this.$prompt('请输入持卡人本人实名手机号', '注册钱包', {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              inputPattern: /^1[3-9]\d{9}$/,
              inputErrorMessage: '请输入正确的手机号',
              inputValue: mobile,
            });
            mobile = value;
          } catch (promptErr) {
            return;
          }
        }
      } else {
        try {
          const { value } = await this.$prompt('请输入持卡人本人实名手机号', '注册钱包', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputPattern: /^1[3-9]\d{9}$/,
            inputErrorMessage: '请输入正确的手机号',
          });
          mobile = value;
        } catch (promptErr) {
          return;
        }
      }

      try {
        const ret = await this.$api.card.walletRegister({ cardId: row.id, mobile });
        if (ret && ret.success) {
          this.$message.success('钱包注册成功');
          row.wallet_merchant_no = ret.data.merchantNo;
          this.$forceUpdate();
          this.fetchData();
        } else {
          this.$message.error(ret?.message || '注册失败');
        }
      } catch (e) {
        this.$message.error(e.message || '注册失败');
      }
    },

    async handleWalletContractSign(row) {
      if (!row.wallet_merchant_no) {
        this.$message.warning('请先注册钱包');
        return;
      }

      try {
        const infoRet = await this.$api.card.walletInfo({ cardId: row.id });
        if (infoRet && infoRet.success) {
          const contractState = infoRet.data.contractState;
          if (contractState === 'SIGNED' || contractState === 'SUCCESS') {
            this.$message.info('该钱包已签约');
            row.contract_signed = true;
            this.$forceUpdate();
            this.fetchData();
            return;
          }
          if (contractState === 'SIGNING' || contractState === 'PROCESSING') {
            this.$message.info('签约处理中，请稍后查询');
            return;
          }
        }

        await this.$confirm('将发送签约短信至持卡人手机，请确认手机号正确？', '申请签约', {
          confirmButtonText: '确认发送',
          cancelButtonText: '取消',
          type: 'info',
        });

        const ret = await this.$api.card.walletContractSign({
          cardId: row.id,
          isSendMsg: 'Y',
        });

        if (ret && ret.success) {
          if (ret.data.signUrl) {
            this.$alert(
              `签约链接已生成，请点击下方链接完成签约（有效期至 ${ret.data.expirationTime || '过期时间未知'}）`,
              '签约链接',
              {
                confirmButtonText: '复制链接',
                dangerouslyUseHTMLString: true,
                message: `<div style="word-break: break-all; color: #409EFF; cursor: pointer;" onclick="navigator.clipboard.writeText('${ret.data.signUrl}')">${ret.data.signUrl}</div><div style="margin-top: 8px; color: #999; font-size: 13px;">点击复制链接后，在浏览器中打开完成签约</div>`,
              }
            );
          } else {
            this.$message.success('签约短信已发送，请查收短信完成签约');
          }
          this.fetchData();
        } else {
          this.$message.error(ret?.message || '签约申请失败');
        }
      } catch (e) {
        if (e !== 'cancel') {
          console.error('[签约申请] 异常:', e);
          this.$message.error(e.message || '签约申请失败');
        }
      }
    },

    async handleWalletInfo(row) {
      if (!row.wallet_merchant_no) {
        this.$message.warning('该卡尚未注册钱包，请先注册');
        return;
      }
      try {
        const ret = await this.$api.card.walletInfo({ cardId: row.id });
        if (ret && ret.success) {
          this.walletInfoData = ret.data;
          this.walletInfoDialogVisible = true;
        } else {
          this.$message.error(ret?.message || '查询失败');
        }
      } catch (e) {
        console.error('[钱包信息查询] 异常:', e);
        this.$message.error(e.message || '查询失败');
      }
    },

    async fetchWalletBalance(row) {
      if (!row.wallet_merchant_no) return;
      try {
        const ret = await this.$api.card.walletBalance({ cardId: row.id });
        const data = ret?.data || {};
        const cashAcc = (data.accountList || []).find(a => a.accountType === '01');
        row.walletBalance = cashAcc ? parseFloat(cashAcc.cashAmount || 0) : 0;
      } catch (e) {
        console.error('查询个人钱包余额失败', e);
        this.$message.error('请求异常');
      }
    },

    async handleWalletTransfer(row) {
      if (!row.wallet_merchant_no) {
        this.$message.warning('请先注册钱包');
        return;
      }
      if (!row.contract_signed) {
        this.$message.warning('请先完成合同签约');
        return;
      }
      this.transferRow = row;
      this.transferAmount = '';
      try {
        const ret = await this.$api.withdraw.platformBalance();
        if (ret && ret.success) {
          const data = ret.data || {};
          this.platformTotalBalance = parseFloat(data.totalAmount || 0).toFixed(2);
          const cashAcc = (data.accountList || []).find(a => a.accountType === '01');
          this.platformAvailableBalance = cashAcc
            ? parseFloat(cashAcc.cashAmount || 0).toFixed(2)
            : '0.00';
        } else {
          this.platformTotalBalance = '0.00';
          this.platformAvailableBalance = '0.00';
        }
      } catch (e) {
        this.platformTotalBalance = '0.00';
        this.platformAvailableBalance = '0.00';
      }
      this.transferDialogVisible = true;
    },

    async submitTransfer() {
      if (!this.transferAmount || parseFloat(this.transferAmount) <= 0) {
        this.$message.error('请输入有效金额');
        return;
      }
      const amount = parseFloat(this.transferAmount);
      if (amount > parseFloat(this.platformAvailableBalance)) {
        this.$message.error('转账金额不能超过平台可提现金额');
        return;
      }
      this.transferSubmitting = true;
      try {
        const ret = await this.$api.card.walletTransfer({
          cardId: this.transferRow.id,
          amount: amount.toFixed(2),
        });
        if (!ret || !ret.success) {
          this.$message.error(ret?.message || '转账失败');
          return;
        }

        const { pageUrl, requestNo, needPwd } = ret.data || {};
        this.transferRequestNo = requestNo || '';

        if (pageUrl && needPwd) {
          this.transferStage = 'pwd';
          this.transferPageUrl = pageUrl;
          this.listenTransferMessage();
          return;
        }

        this.$message.success('转账成功');
        this.transferDialogVisible = false;
        this.fetchData();
      } catch (e) {
        console.error('转账异常', e);
        this.$message.error('转账异常，请稍后重试');
      } finally {
        this.transferSubmitting = false;
      }
    },

    // ★ 新增：模板里的"查询结果"按钮对应方法（原代码缺失）
    async queryTransferResult() {
      if (!this.transferRequestNo) {
        this.$message.warning('缺少转账请求号');
        return;
      }
      try {
        const ret = await this.$api.card.walletTransferQuery({ requestNo: this.transferRequestNo });
        const data = ret?.data || {};
        const isSuccess = data.state === 'SUCCESS' || data.subCode === 'COM000';
        const isFailed = data.state === 'FAIL' || data.state === 'FAILED';
        if (isSuccess) {
          this.$message.success('转账成功');
          this.transferDialogVisible = false;
          this.fetchData();
        } else if (isFailed) {
          this.$message.error('转账失败');
        } else {
          this.$message.info('转账处理中，请稍后再次查询');
        }
      } catch (e) {
        console.error('查询转账结果异常:', e);
        this.$message.error('查询异常，请稍后重试');
      }
    },

    listenTransferMessage() {
      if (this._transferMsgListener) {
        window.removeEventListener('message', this._transferMsgListener);
      }
      this._transferMsgListener = (event) => {
        if (!event.data || event.data.type !== 'transfer-complete') return;
        this.transferStage = 'waiting';
        this.startTransferPolling();
      };
      window.addEventListener('message', this._transferMsgListener);
    },

    openPwdInNewTab() {
      if (this.transferPageUrl) {
        window.open(this.transferPageUrl, '_blank', 'width=800,height=700');
      }
    },

    startTransferPolling() {
      if (this.transferPollTimer) clearInterval(this.transferPollTimer);
      let count = 0;
      const loadingMsg = this.$message({
        message: '正在确认转账结果...',
        type: 'info',
        duration: 0,
      });
      this.transferPollTimer = setInterval(async () => {
        count++;
        if (count > 15) {
          clearInterval(this.transferPollTimer);
          this.transferPollTimer = null;
          loadingMsg.close();
          this.$message.warning('查询超时，请稍后刷新余额查看');
          return;
        }
        try {
          const ret = await this.$api.card.walletTransferQuery({ requestNo: this.transferRequestNo });
          const data = ret?.data || {};
          const isSuccess = data.state === 'SUCCESS' || data.subCode === 'COM000';
          const isFailed = data.state === 'FAIL' || data.state === 'FAILED';
          if (isSuccess) {
            clearInterval(this.transferPollTimer);
            this.transferPollTimer = null;
            loadingMsg.close();
            this.$message.success('转账成功');
            this.transferDialogVisible = false;
            this.fetchData();
          } else if (isFailed) {
            clearInterval(this.transferPollTimer);
            this.transferPollTimer = null;
            loadingMsg.close();
            this.$message.error('转账失败');
          }
        } catch (e) {
          // 忽略单次失败
        }
      }, 2000);
    },

    resetTransferDialog() {
      this.transferStage = 'input';
      this.transferPageUrl = '';
      this.transferAmount = '';
      this.transferRequestNo = '';
      if (this.transferPollTimer) {
        clearInterval(this.transferPollTimer);
        this.transferPollTimer = null;
      }
      if (this._transferMsgListener) {
        window.removeEventListener('message', this._transferMsgListener);
        this._transferMsgListener = null;
      }
    },

    async handleTransferDialogClose(done) {
      if (this.transferStage === 'waiting') {
        try {
          await this.$confirm('转账正在处理中，关闭后可在卡号管理页面点击"钱包余额"查看结果。确认关闭？', '提示', {
            confirmButtonText: '确认关闭',
            cancelButtonText: '继续等待',
            type: 'warning',
          });
          done();
        } catch (e) {
          // 用户取消关闭
        }
      } else {
        done();
      }
    },

    async handleWalletBind(row) {
      if (!row.wallet_merchant_no) {
        this.$message.warning('请先注册钱包');
        return;
      }

      try {
        const infoRet = await this.$api.card.walletInfo({ cardId: row.id });
        if (infoRet && infoRet.success) {
          const contractState = infoRet.data.contractState;
          if (contractState !== 'SIGNED' && contractState !== 'SUCCESS') {
            if (contractState === 'SIGNING' || contractState === 'PROCESSING') {
              this.$message.warning('签约处理中，请稍后再试');
            } else {
              this.$message.warning('该钱包尚未签约，请先完成签约');
            }
            return;
          }
          row.contract_signed = true;
          this.$forceUpdate();
        }
      } catch (e) {
        this.$message.error('查询签约状态失败，请稍后重试');
        return;
      }

      let bankMobile = row.phone || '';
      try {
        const { value } = await this.$prompt('请输入银行预留手机号', '绑定银行卡', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputPattern: /^1[3-9]\d{9}$/,
          inputErrorMessage: '请输入正确的手机号',
          inputValue: bankMobile,
        });
        bankMobile = value;
      } catch (e) {
        return;
      }

      try {
        const ret = await this.$api.card.walletBind({
          cardId: row.id,
          bankMobile: bankMobile,
        });
        if (ret && ret.success) {
          if (ret.data.bindRequestNo) {
            row.bindRequestNo = ret.data.bindRequestNo;
          }
          if (ret.data.authSn) {
            try {
              const { value: smsCode } = await this.$prompt('请输入短信验证码', '短信验证', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                inputPattern: /^\d{4,6}$/,
                inputErrorMessage: '请输入4-6位数字验证码',
              });
              const confirmRet = await this.$api.card.walletSmsConfirm({
                cardId: row.id,
                authSn: ret.data.authSn,
                smsCode: smsCode,
                requestNo: row.bindRequestNo,
              });
              if (confirmRet && confirmRet.success) {
                this.$message.success('绑卡成功');
                row.link_id = confirmRet.data.linkId;
                this.$forceUpdate();
                this.fetchData();
              } else {
                this.$message.error(confirmRet?.message || '短信验证失败');
              }
            } catch (e) {
              return;
            }
          } else if (ret.data.linkId) {
            this.$message.success('绑卡成功');
            row.link_id = ret.data.linkId;
            this.$forceUpdate();
            this.fetchData();
          }
        } else {
          this.$message.error(ret?.message || '绑卡失败');
        }
      } catch (e) {
        this.$message.error(e.message || '绑卡失败');
      }
    },

    async fetchPlatformBalance() {
      if (this.balanceLoading) return;
      this.balanceLoading = true;
      try {
        const ret = await this.$api.withdraw.platformBalance();
        if (ret && ret.success) {
          const balance = ret?.data?.totalAmount ?? 0;
          this.platformBalance = parseFloat(balance) || 0;
          this.platformMerchantNo = ret?.data?.merchantNo || '';
          this.platformBalanceDetail = ret.data;
          this.balanceDialogVisible = true;
        } else {
          this.$message.error(ret?.message || '查询平台余额失败');
        }
      } catch (e) {
        console.error('获取平台余额失败', e);
        this.$message.error('请求异常，请稍后重试');
      } finally {
        this.balanceLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.table-wrapper .el-table {
  min-width: 2200px;
}

.action-btns {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.action-btns .el-button + .el-button {
  margin-left: 0;
}
.search-card { margin-bottom: 16px; }
.search-card .el-form-item { margin-bottom: 0; }
.table-card :deep(.el-card__header) { padding: 10px 16px; border-bottom: 1px solid #e4e7ed; }
.table-header { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.table-header-left { display: flex; align-items: center; gap: 8px; }
.table-header-right { display: flex; align-items: center; gap: 4px; }
.pagination-wrapper { display: flex; justify-content: center; margin-top: 16px; }
.withdraw-link { color: #409EFF; cursor: pointer; font-size: 13px; font-weight: 500; }
.withdraw-link:hover { color: #66b1ff; }
:deep(.el-radio.is-bordered) { padding: 8px 18px; margin-right: 12px; border-radius: 4px; border: 1px solid #dcdfe6; transition: border-color 0.2s; }
:deep(.el-radio.is-bordered.is-checked) { border-color: #409EFF; box-shadow: 0 0 0 1px #409EFF; }
:deep(.el-radio.is-bordered .el-radio__label) { font-size: 14px; color: #606266; }
:deep(.el-radio.is-bordered.is-checked .el-radio__label) { color: #409EFF; }
:deep(.card-form-dialog .el-dialog__body) {
  padding: 16px 24px 8px;
  max-height: 68vh;
  overflow: hidden;
}
:deep(.card-form-dialog .el-form) {
  max-height: 62vh;
  overflow-y: auto;
  padding-right: 8px;
}
:deep(.card-form-dialog .el-form::-webkit-scrollbar) {
  width: 6px;
}
:deep(.card-form-dialog .el-form::-webkit-scrollbar-thumb) {
  background: #dcdfe6;
  border-radius: 3px;
}
:deep(.card-form-dialog .el-form::-webkit-scrollbar-thumb:hover) {
  background: #c0c4cc;
}
.avatar-uploader .el-upload { border: 1px dashed #d9d9d9; border-radius: 6px; cursor: pointer; position: relative; overflow: hidden; }
.avatar-uploader .el-upload:hover { border-color: #409EFF; }
.avatar-uploader-icon { font-size: 28px; color: #8c939d; width: 80px; height: 80px; line-height: 80px; text-align: center; }
.avatar { width: 80px; height: 80px; display: block; }
</style>